import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Cloud, Plus, Trash2, Edit, UserPlus, UserMinus, Calendar, DollarSign,
  AlertCircle, ExternalLink, Users
} from 'lucide-react';

const fmtCur = (n, c = 'AED') => `${c} ${(n || 0).toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function SaaSSubscriptionsPage() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [subs, setSubs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [editModal, setEditModal] = useState({ open: false, data: null });
  const [detailModal, setDetailModal] = useState({ open: false, sub: null });
  const [seatModal, setSeatModal] = useState({ open: false, sub: null, employee_id: '', login_email: '', notes: '' });

  const fetchDashboard = useCallback(async () => {
    try { const r = await apiClient.get('/saas/dashboard'); setDashboard(r.data); } catch {/* ignore */ }
  }, []);
  const fetchSubs = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (search) p.set('search', search);
      if (statusFilter !== 'all') p.set('status', statusFilter);
      const r = await apiClient.get(`/saas?${p.toString()}`);
      setSubs(Array.isArray(r.data) ? r.data : []);
    } catch { setSubs([]); }
  }, [search, statusFilter]);
  const fetchEmployees = useCallback(async () => {
    try {
      const r = await apiClient.get('/hr/employees?status=active&limit=500');
      const list = Array.isArray(r.data) ? r.data : (r.data?.employees || []);
      setEmployees(list.filter(e => (e.employment_status || 'active') === 'active'));
    } catch { setEmployees([]); }
  }, []);
  useEffect(() => { fetchDashboard(); fetchEmployees(); }, [fetchDashboard, fetchEmployees]);
  useEffect(() => { if (tab === 'list') fetchSubs(); }, [tab, fetchSubs]);

  async function saveSub() {
    const d = editModal.data;
    if (!d?.name?.trim()) return toast.error('Name required');
    try {
      const body = { ...d, cost: parseFloat(d.cost) || 0, seats_total: parseInt(d.seats_total) || 1 };
      if (d.id) await apiClient.put(`/saas/${d.id}`, body);
      else await apiClient.post('/saas', body);
      toast.success('Saved');
      setEditModal({ open: false, data: null });
      fetchSubs(); fetchDashboard();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }
  async function delSub(id) {
    if (!window.confirm('Delete this subscription?')) return;
    try { await apiClient.delete(`/saas/${id}`); fetchSubs(); fetchDashboard(); }
    catch { toast.error('Failed'); }
  }
  async function loadDetail(id) {
    try {
      const r = await apiClient.get(`/saas/${id}`);
      setDetailModal({ open: true, sub: r.data });
    } catch { toast.error('Failed'); }
  }
  async function assignSeat() {
    const m = seatModal;
    if (!m.employee_id) return toast.error('Pick employee');
    try {
      await apiClient.post(`/saas/${m.sub.id}/seats/assign`, {
        employee_id: m.employee_id, login_email: m.login_email, notes: m.notes,
      });
      toast.success('Seat assigned');
      setSeatModal({ open: false, sub: null, employee_id: '', login_email: '', notes: '' });
      loadDetail(m.sub.id); fetchSubs(); fetchDashboard();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }
  async function unassignSeat(subId, assignmentId) {
    if (!window.confirm('Revoke this seat?')) return;
    try {
      await apiClient.delete(`/saas/${subId}/seats/${assignmentId}`);
      loadDetail(subId); fetchSubs(); fetchDashboard();
    } catch { toast.error('Failed'); }
  }

  return (
    <div className="space-y-6" data-testid="saas-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-600 flex items-center justify-center">
            <Cloud className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">SaaS Subscriptions</h1>
            <p className="text-muted-foreground text-sm">Track every recurring software cost and who uses what.</p>
          </div>
        </div>
        <Button onClick={() => setEditModal({ open: true, data: { billing_cycle: 'monthly', seats_total: 1, status: 'active', auto_renew: true, currency: 'AED' } })} data-testid="add-sub-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Subscription
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="list">All Subscriptions</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-4">
          {!dashboard ? <p className="text-center text-muted-foreground py-12">Loading…</p> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat title="Subscriptions" value={dashboard.total_subscriptions} sub={`${dashboard.active_count} active`} />
                <Stat title="Monthly Spend" value={fmtCur(dashboard.monthly_total)} sub="across all subs" />
                <Stat title="Annual Spend" value={fmtCur(dashboard.annual_total)} sub="if continued as-is" />
                <Stat title="Seat Utilization" value={`${dashboard.seats_utilization_pct}%`} sub={`${dashboard.seats_used}/${dashboard.seats_total} seats`} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" />Renewing Soon (30 days)</CardTitle></CardHeader>
                  <CardContent>
                    {(dashboard.renewing_soon || []).length === 0 ? <p className="text-sm text-muted-foreground py-4">No renewals in the next 30 days.</p> :
                      <div className="space-y-2">
                        {dashboard.renewing_soon.map(s => (
                          <button key={s.id} onClick={() => loadDetail(s.id)} className="w-full text-left p-2 rounded-md border hover:border-primary/40 flex items-center justify-between">
                            <div className="text-sm">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-xs text-muted-foreground">{s.provider} · {fmtCur(s.cost, s.currency)} / {s.billing_cycle}</p>
                            </div>
                            <Badge variant="outline" className={s.days_to_renewal <= 7 ? 'bg-red-500/10 text-red-700' : 'bg-amber-500/10 text-amber-700'}>
                              {s.days_to_renewal}d
                            </Badge>
                          </button>
                        ))}
                      </div>
                    }
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4" />Spend by Provider</CardTitle></CardHeader>
                  <CardContent>
                    {(dashboard.by_provider || []).length === 0 ? <p className="text-sm text-muted-foreground py-4">No data yet.</p> :
                      <div className="space-y-2">
                        {dashboard.by_provider.map(p => (
                          <div key={p.provider} className="flex justify-between text-sm py-1">
                            <span>{p.provider} <span className="text-xs text-muted-foreground">({p.count})</span></span>
                            <span className="font-mono text-emerald-700">{fmtCur(p.monthly_cost)}/mo</span>
                          </div>
                        ))}
                      </div>
                    }
                  </CardContent>
                </Card>
              </div>
              {dashboard.expired?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2 text-red-700"><AlertCircle className="h-4 w-4" />Expired (renewal_date in past)</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {dashboard.expired.map(s => (
                        <div key={s.id} className="flex justify-between text-sm">
                          <span>{s.name}</span>
                          <Badge variant="outline" className="bg-red-500/10 text-red-700">{Math.abs(s.days_to_renewal)} days overdue</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="list" className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <Input placeholder="Search name/provider…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-sm" data-testid="saas-search" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Provider</TableHead><TableHead>Cycle</TableHead>
                <TableHead>Cost</TableHead><TableHead>Seats</TableHead><TableHead>Renewal</TableHead>
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {subs.map(s => (
                  <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                    <TableCell className="font-medium text-sm">{s.name}</TableCell>
                    <TableCell className="text-sm">{s.provider}</TableCell>
                    <TableCell className="text-xs">{s.billing_cycle}</TableCell>
                    <TableCell className="text-xs font-mono">{fmtCur(s.cost, s.currency)}</TableCell>
                    <TableCell className="text-xs">{s.seats_used} / {s.seats_total}</TableCell>
                    <TableCell className="text-xs">
                      {s.renewal_date || '—'}
                      {s.days_to_renewal !== null && s.days_to_renewal >= 0 && s.days_to_renewal <= 30 &&
                        <Badge variant="outline" className="ml-1 bg-amber-500/10 text-amber-700 text-xs">{s.days_to_renewal}d</Badge>}
                    </TableCell>
                    <TableCell><Badge variant="outline">{s.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => loadDetail(s.id)}><Users className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => setEditModal({ open: true, data: s })}><Edit className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => delSub(s.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {subs.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No subscriptions.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Edit modal */}
      <Dialog open={editModal.open} onOpenChange={o => !o && setEditModal({ open: false, data: null })}>
        <DialogContent className="max-w-xl" data-testid="sub-modal">
          <DialogHeader><DialogTitle>{editModal.data?.id ? 'Edit' : 'New'} Subscription</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={editModal.data?.name || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, name: e.target.value } })} data-testid="sub-name" placeholder="e.g. Microsoft 365" /></div>
              <div><Label>Provider</Label><Input value={editModal.data?.provider || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, provider: e.target.value } })} placeholder="Microsoft" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Cost</Label><Input type="number" step="0.01" value={editModal.data?.cost || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, cost: e.target.value } })} data-testid="sub-cost" /></div>
              <div><Label>Currency</Label><Input value={editModal.data?.currency || 'AED'} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, currency: e.target.value } })} /></div>
              <div>
                <Label>Billing</Label>
                <Select value={editModal.data?.billing_cycle || 'monthly'} onValueChange={v => setEditModal({ ...editModal, data: { ...editModal.data, billing_cycle: v } })}>
                  <SelectTrigger data-testid="sub-cycle"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Seats Total</Label><Input type="number" min="1" value={editModal.data?.seats_total || 1} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, seats_total: e.target.value } })} data-testid="sub-seats" /></div>
              <div><Label>Renewal Date</Label><Input type="date" value={editModal.data?.renewal_date || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, renewal_date: e.target.value } })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Owner</Label>
                <Select value={editModal.data?.owner_id || ''} onValueChange={v => {
                  const emp = employees.find(e => e.id === v);
                  setEditModal({ ...editModal, data: { ...editModal.data, owner_id: v, owner_name: emp?.full_name || '' } });
                }}>
                  <SelectTrigger><SelectValue placeholder="Pick owner" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Website</Label><Input value={editModal.data?.website || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, website: e.target.value } })} placeholder="https://…" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Login email (shared)</Label><Input value={editModal.data?.login_email || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, login_email: e.target.value } })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={editModal.data?.status || 'active'} onValueChange={v => setEditModal({ ...editModal, data: { ...editModal.data, status: v } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={editModal.data?.notes || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, notes: e.target.value } })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditModal({ open: false, data: null })}>Cancel</Button><Button onClick={saveSub} data-testid="save-sub">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / seat assignment modal */}
      <Dialog open={detailModal.open} onOpenChange={o => !o && setDetailModal({ open: false, sub: null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sub-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Cloud className="h-5 w-5" />{detailModal.sub?.name}</DialogTitle>
            <DialogDescription>{detailModal.sub?.provider} · {fmtCur(detailModal.sub?.cost, detailModal.sub?.currency)} / {detailModal.sub?.billing_cycle}</DialogDescription>
          </DialogHeader>
          {detailModal.sub && (
            <>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="border rounded p-2 text-center"><p className="text-muted-foreground">Seats Used</p><p className="font-bold text-lg">{detailModal.sub.seats_used}/{detailModal.sub.seats_total}</p></div>
                <div className="border rounded p-2 text-center"><p className="text-muted-foreground">Monthly Eq.</p><p className="font-bold text-lg">{fmtCur(detailModal.sub.monthly_equivalent_cost)}</p></div>
                <div className="border rounded p-2 text-center"><p className="text-muted-foreground">Renewal</p><p className="font-bold">{detailModal.sub.renewal_date || '—'}</p></div>
              </div>
              <div className="space-y-2 mt-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Seat Assignments</h3>
                  <Button size="sm" onClick={() => setSeatModal({ open: true, sub: detailModal.sub, employee_id: '', login_email: '', notes: '' })} data-testid="assign-seat-btn"><UserPlus className="h-4 w-4 mr-1.5" />Assign Seat</Button>
                </div>
                {detailModal.sub.assignments?.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No seats assigned yet.</p>}
                <div className="space-y-1">
                  {(detailModal.sub.assignments || []).map(a => (
                    <div key={a.id} className="flex items-center justify-between p-2 border rounded text-sm">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{a.employee_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{a.login_email || '—'}</p>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => unassignSeat(detailModal.sub.id, a.id)} title="Revoke"><UserMinus className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              </div>
              {detailModal.sub.website && <a href={detailModal.sub.website} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline mt-3 inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" />Visit dashboard</a>}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Seat assign modal */}
      <Dialog open={seatModal.open} onOpenChange={o => !o && setSeatModal({ open: false, sub: null, employee_id: '', login_email: '', notes: '' })}>
        <DialogContent className="max-w-md" data-testid="seat-modal">
          <DialogHeader><DialogTitle>Assign Seat: {seatModal.sub?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employee *</Label>
              <Select value={seatModal.employee_id} onValueChange={v => setSeatModal({ ...seatModal, employee_id: v })}>
                <SelectTrigger data-testid="seat-employee"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Login email for this user</Label><Input value={seatModal.login_email} onChange={e => setSeatModal({ ...seatModal, login_email: e.target.value })} /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={seatModal.notes} onChange={e => setSeatModal({ ...seatModal, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSeatModal({ open: false, sub: null, employee_id: '', login_email: '', notes: '' })}>Cancel</Button><Button onClick={assignSeat} data-testid="submit-seat">Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ title, value, sub }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold mt-0.5">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}
